import type { LoanPolicy, LoanPolicyInput, PatronCategory, RecordType } from "@/api/types";
import { RECORD_TYPE_PLURAL_LABELS } from "@/lib/recordTypes";

const euroFormatter = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });

export function formatEuros(cents: number): string {
  return euroFormatter.format(cents / 100);
}

/** "0,2" or "0.20" typed by an administrator, in cents; null when not a valid amount. */
export function parseEuros(value: string): number | null {
  const normalized = value.trim().replace(",", ".");
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",");
}

export function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${String(count)} ${count > 1 ? pluralForm : singular}`;
}

export function scopeLabel(scope: { itemType: RecordType | null; locationName: string | null }): {
  what: string;
  where: string;
} {
  return {
    what: scope.itemType ? RECORD_TYPE_PLURAL_LABELS[scope.itemType] : "Tous les documents",
    where: scope.locationName ?? "Tous les sites",
  };
}

export interface RuleDifference {
  label: string;
  value: string;
  /** Category default it replaces, when the category has an equivalent setting */
  defaultValue: string | null;
}

/**
 * What an exception changes compared with the category defaults, so the list
 * shows the substance of each rule rather than every number.
 */
export function policyDifferences(policy: LoanPolicy, category: PatronCategory): RuleDifference[] {
  const differences: RuleDifference[] = [];
  const days = (n: number) => plural(n, "jour");

  if (!policy.loanable) {
    return [{ label: "Prêt", value: "exclu", defaultValue: null }];
  }
  if (policy.loanDays !== category.defaultLoanDays) {
    differences.push({
      label: "Durée",
      value: days(policy.loanDays),
      defaultValue: days(category.defaultLoanDays),
    });
  }
  if (policy.maxLoans !== null && policy.maxLoans !== category.maxLoans) {
    differences.push({
      label: "Quota",
      value: String(policy.maxLoans),
      defaultValue: String(category.maxLoans),
    });
  }
  if (!policy.renewalsAllowed) {
    differences.push({ label: "Prolongation", value: "exclue", defaultValue: null });
  } else {
    if (policy.maxRenewals !== category.maxRenewals) {
      differences.push({
        label: "Prolongations",
        value: String(policy.maxRenewals),
        defaultValue: String(category.maxRenewals),
      });
    }
    // Category defaults renew for the loan duration
    if (policy.renewalDays !== category.defaultLoanDays) {
      differences.push({
        label: "Durée prolongée",
        value: days(policy.renewalDays),
        defaultValue: days(category.defaultLoanDays),
      });
    }
    if (policy.renewalWindowDays !== category.renewalWindowDays) {
      differences.push({
        label: "Prolongeable",
        value: `${days(policy.renewalWindowDays)} avant`,
        defaultValue: `${days(category.renewalWindowDays)} avant`,
      });
    }
  }
  if (!policy.reservationsAllowed) {
    differences.push({ label: "Réservation", value: "exclue", defaultValue: null });
  }
  if (policy.finePerDayInCents !== category.finePerDayInCents) {
    differences.push({
      label: "Amende",
      value: `${formatEuros(policy.finePerDayInCents)}/j`,
      defaultValue: `${formatEuros(category.finePerDayInCents)}/j`,
    });
  }
  if (policy.maxFineInCents !== category.maxFineInCents) {
    differences.push({
      label: "Plafond",
      value: formatFineCap(policy.maxFineInCents),
      defaultValue: formatFineCap(category.maxFineInCents),
    });
  }
  if (policy.gracePeriodDays > 0) {
    differences.push({
      label: "Tolérance",
      value: days(policy.gracePeriodDays),
      defaultValue: null,
    });
  }

  return differences;
}

export function formatFineCap(cents: number): string {
  return cents === 0 ? "sans plafond" : formatEuros(cents);
}

/** A new exception starts from the category defaults: the administrator only changes what differs. */
export function newPolicyFromCategory(category: PatronCategory): LoanPolicyInput {
  return {
    patronCategoryId: category.id,
    itemType: null,
    locationId: null,
    loanDays: category.defaultLoanDays,
    maxLoans: null,
    maxRenewals: category.maxRenewals,
    renewalDays: category.defaultLoanDays,
    renewalWindowDays: category.renewalWindowDays,
    finePerDayInCents: category.finePerDayInCents,
    maxFineInCents: category.maxFineInCents,
    gracePeriodDays: 0,
    loanable: true,
    renewalsAllowed: true,
    reservationsAllowed: true,
    active: true,
    notes: null,
  };
}

export type FieldSpec = { kind: "int"; min: number; max: number } | { kind: "money" };

/** Client-side check of a typed value, mirroring the API's validation constraints. */
export function readField(
  raw: string,
  spec: FieldSpec
): { ok: true; value: number } | { ok: false; error: string } {
  if (spec.kind === "money") {
    const cents = parseEuros(raw);
    return cents === null
      ? { ok: false, error: "Montant invalide (ex. 0,20)" }
      : { ok: true, value: cents };
  }

  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return { ok: false, error: "Nombre entier attendu" };
  const value = Number(trimmed);
  if (value < spec.min || value > spec.max) {
    return { ok: false, error: `Entre ${String(spec.min)} et ${String(spec.max)}` };
  }
  return { ok: true, value };
}

/**
 * Validate a whole draft at once: every value when it is valid, otherwise the
 * error of each invalid field.
 */
export function readDraft<K extends string>(
  draft: Record<K, string>,
  specs: Record<K, FieldSpec>
): { values: Record<K, number> | null; errors: Partial<Record<K, string>> } {
  const values = {} as Record<K, number>;
  const errors: Partial<Record<K, string>> = {};

  for (const key of Object.keys(specs) as K[]) {
    const result = readField(draft[key], specs[key]);
    if (result.ok) values[key] = result.value;
    else errors[key] = result.error;
  }

  return { values: Object.keys(errors).length === 0 ? values : null, errors };
}

/** Category defaults, with the bounds of PatronCategoryResource. */
export const CATEGORY_RULE_SPECS = {
  defaultLoanDays: { kind: "int", min: 1, max: 365 },
  maxLoans: { kind: "int", min: 1, max: 500 },
  maxReservations: { kind: "int", min: 0, max: 100 },
  maxRenewals: { kind: "int", min: 0, max: 20 },
  renewalWindowDays: { kind: "int", min: 0, max: 365 },
  finePerDayInCents: { kind: "money" },
  maxFineInCents: { kind: "money" },
} as const satisfies Record<string, FieldSpec>;

/** Exception numbers, with the bounds of LoanPolicyResource. */
export const POLICY_RULE_SPECS = {
  loanDays: { kind: "int", min: 1, max: 365 },
  maxRenewals: { kind: "int", min: 0, max: 20 },
  renewalDays: { kind: "int", min: 1, max: 365 },
  renewalWindowDays: { kind: "int", min: 0, max: 365 },
  gracePeriodDays: { kind: "int", min: 0, max: 90 },
  finePerDayInCents: { kind: "money" },
  maxFineInCents: { kind: "money" },
} as const satisfies Record<string, FieldSpec>;

export function toDraft<K extends string>(
  values: Record<K, number>,
  specs: Record<K, FieldSpec>
): Record<K, string> {
  const draft = {} as Record<K, string>;
  for (const key of Object.keys(specs) as K[]) {
    draft[key] = specs[key].kind === "money" ? centsToInput(values[key]) : String(values[key]);
  }
  return draft;
}
